import React, { useCallback, useMemo } from "react";
import { CheckBadgeIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { RfiActionToastProvider } from "../../contexts/RfiActionToastContext";
import CustomTable from "../CustomTable";
import ScopeWithAdditionalTags from "../ScopeWithAdditionalTags";
import { shouldShowAdditionalScopeOnLibraryItem } from "../../utils/scopeDisplayUtils";
import { MenuDropdownAccordingToStatuses } from "../RFIsComponents/MenuDropdownAccordingToStatuses";
import LibraryRequestViewDetailsMenu from "./LibraryRequestViewDetailsMenu";
import { resolveLibraryRequestDetailsPath } from "../../utils/libraryRequestDetailsView";
import GridStatusDotCell from "../GridStatusDotCell";
import { formatGridDate } from "../../utils/myRfiGridRowMapper";
import {
  getLibraryRequestArchiveCommentDisplay,
  getLibraryRequestCycleTimeDisplay,
  getLibraryRequestDocumentName,
  getLibraryRequestLocationDisplay,
  getLibraryRequestPublishedDisplay,
} from "../../utils/libraryRequestsGridMapper";
import {
  getRfiGridCellPaddingClass,
  SIZE_TABLE_COL_MIN,
  SIZE_TABLE_TEXT_COL_MAX,
} from "../RFIs/rfiGridTableStyles";
import { Tooltip } from "@mui/material";
import RiskSummaryCell from "../RFIs/RiskSummaryCell";
import { useRiskSummariesByIds } from "../../custom-hooks/queries";
import {
  extractRiskRowIds,
  getLibraryRequestRiskSummary,
  makeRiskSummarySortComparator,
} from "../../utils/riskSummaryGridUtils";
import { useIsOrgRiskScoringEnabled } from "../../utils/orgFeatureGates";
import { isDiligentAdminUserRole, useUserRole } from "../../utils/helperMethods";
import { useNavigate } from "react-router-dom";
import GridOrgNameLink from "../common/GridOrgNameLink";
import { useGridOrgNameNavigation } from "../../custom-hooks/useGridOrgNameNavigation";

/** Tight but scroll-safe metadata columns; paired with `minWidth` in column defs */
const LIBRARY_REQ_METADATA_COL_WIDTH = "4%";

/**
 * Intermediate table for Library Requests (sponsor vs vendor vs admin).
 * @param {'sponsor'|'provider'|'admin'} [props.audience] — sponsor: Vendor column + menu; provider: Sponsor column + approve/reject; admin: Sponsor + Vendor + menu.
 */
export default function LibraryRequestsGridTable({
  data,
  sortConfig,
  onSort,
  refetch,
  isSearchActive = false,
  emptyMessage = "No documents found",
  emptyDescription = "No documents are available at this time.",
  className = "py-0 sm:py-0",
  rowKey = "document_id",
  audience = "sponsor",
  approve,
  reject,
  approveError,
  rejectError,
  rejectIsError,
}) {
  const navigate = useNavigate();
  const {
    navigateToHref,
    sponsorHrefForLibraryItem,
    vendorHrefForLibraryItem,
  } = useGridOrgNameNavigation();
  // 14029 — Library Requests / My Documents grids. Risk only applies to RFI
  // (VQQ) rows; audit/VQA rows always render "--". Visibility: admin always,
  // sponsor/vendor when entitled.
  const userRole = useUserRole();
  const isAdmin = isDiligentAdminUserRole(userRole);
  const isRiskEnabled = useIsOrgRiskScoringEnabled();
  const showRiskColumn = isAdmin || isRiskEnabled;
  const rowIds = useMemo(() => extractRiskRowIds(data), [data]);
  const { data: summariesByRowId } = useRiskSummariesByIds(rowIds, {
    enabled: showRiskColumn && rowIds.length > 0,
  });
  const summaryMap = summariesByRowId ?? {};
  const renderDocumentTypeCell = useCallback((item) => {
    const isRfi = String(item?.document_type || "").toLowerCase() === "rfi";
    return (
      <Tooltip title={isRfi ? "VQQ" : "Audit"} arrow placement="bottom">
        <div className="flex min-w-0 w-full max-w-full items-center gap-2">
          {isRfi ? (
            <div className="flex-shrink-0 rounded bg-indigo-600 p-1 text-white">
              <CheckCircleIcon className="h-5 w-5" aria-hidden />
            </div>
          ) : (
            <div className="flex-shrink-0 rounded bg-rose-600 p-1 text-white">
              <CheckBadgeIcon className="h-5 w-5" aria-hidden />
            </div>
          )}
        </div>
      </Tooltip>
    );
  }, []);

  const renderDocumentNameCell = useCallback((item, onlyIcon = false) => {
    const isRfi = String(item?.document_type || "").toLowerCase() === "rfi";
    const title = !isRfi ? item?.report_type : getLibraryRequestDocumentName(item);
    return (
      <div className="flex min-w-0 w-full max-w-full items-center gap-2">
        {isRfi ? (
          <div className="flex-shrink-0 rounded bg-indigo-600 p-1 text-white">
            <CheckCircleIcon className="h-5 w-5" aria-hidden />
          </div>
        ) : (
          <div className="flex-shrink-0 rounded bg-rose-600 p-1 text-white">
            <CheckBadgeIcon className="h-5 w-5" aria-hidden />
          </div>
        )}
        {!onlyIcon && <div
          className="min-w-0 flex-1 break-words text-sm text-gray-900"
          title={title || undefined}
        >
          {title || <span className="text-gray-400">--</span>}
        </div>}
      </div>
    );
  }, []);

  const columns = useMemo(() => {
    const sponsorColumn = {
      key: "sponsor_name",
      header: "Sponsor",
      sortable: true,
      width: audience === "admin" ? "8%" : "9%",
      minWidth: SIZE_TABLE_COL_MIN.org,
      contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
      cellClassName: "text-gray-900 font-medium",
      render: (item) => (
        <div>
          <GridOrgNameLink
            name={item?.sponsor_name || "--"}
            href={sponsorHrefForLibraryItem(item)}
            onNavigate={navigateToHref}
          />
          {item?.requested_by ? (
            <div className="text-sm font-normal text-gray-600">
              {item.requested_by}
            </div>
          ) : null}
        </div>
      ),
    };

    const vendorColumn = {
      key: "provider_name",
      header: "Vendor",
      sortable: true,
      width: audience === "admin" ? "8%" : "7%",
      minWidth: SIZE_TABLE_COL_MIN.org,
      contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
      cellClassName: "text-gray-900 font-medium",
      render: (item) => (
        <GridOrgNameLink
          name={item?.provider_name || "--"}
          href={vendorHrefForLibraryItem(item)}
          onNavigate={navigateToHref}
        />
      ),
    };

    const orgColumns =
      audience === "admin"
        ? [sponsorColumn, vendorColumn]
        : audience === "provider"
          ? [sponsorColumn]
          : [vendorColumn];

    // Admin-only: aligns with Assessment Request and admin VQQ grids.
    const archiveCommentColumn =
      audience === "admin"
        ? {
            key: "archive_comment",
            header: "Archive Comment",
            sortable: false,
            width: "10%",
            minWidth: SIZE_TABLE_COL_MIN.document,
            contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
            cellClassName: "text-gray-500",
            render: (item) => {
              const text = getLibraryRequestArchiveCommentDisplay(item);
              return text ? (
                <span
                  className="italic text-xs line-clamp-2"
                  title={text}
                >
                  {text}
                </span>
              ) : (
                <span className="text-gray-300">--</span>
              );
            },
          }
        : null;

    const actionsColumn = {
      key: "actions",
      header: "Actions",
      sortable: false,
      minWidth: SIZE_TABLE_COL_MIN.actions,
      align: "right",
      cellClassName: "text-right",
      excludeFromRowClassName: true,
      render: (item) => {
        const detailsPath = resolveLibraryRequestDetailsPath(item);
        if (audience === "provider") {
          if (
            item?.request_status === "Rejected" ||
            item?.request_status === "Released"
          ) {
            return <LibraryRequestViewDetailsMenu path={detailsPath} />;
          }
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <MenuDropdownAccordingToStatuses
                status={item?.request_status}
                type={item?.document_type}
                docId={item?.document_id}
                isDocLibrary={true}
                isMyRFI={false}
                sponsorRfiResponseId={item?.entity_id}
                questionnaireId={item?.hc_questionnaire_id}
                isMyDocs={true}
                details={item}
                refetch={refetch}
                isProviderLibraryVendor={true}
                approve={approve}
                reject={reject}
                approveError={approveError}
                rejectError={rejectError}
                rejectIsError={rejectIsError}
                libraryRequestDetailsPath={detailsPath}
              />
            </div>
          );
        }
        // Diligent Admin: always show actions (Archive, etc.); the sponsor-only guard
        // below would hide the menu for Pending VQA / RFI with no questionnaire, which
        // blocks the ticket workflow on Library Requests.
        if (audience === "admin") {
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <MenuDropdownAccordingToStatuses
                status={item?.request_status}
                type={item?.document_type}
                docId={item?.document_id}
                isDocLibrary={true}
                isMyRFI={false}
                sponsorRfiResponseId={item?.entity_id}
                questionnaireId={item?.hc_questionnaire_id}
                isMyDocs={true}
                details={item}
                refetch={refetch}
                isLibraryBackendAdmin={true}
                libraryRequestId={item?.request_id}
                libraryRequestDetailsPath={detailsPath}
              />
            </div>
          );
        }
        // Pending audit reports have no sponsor cancel path; VQQ (RFI) rows always get
        // the menu — cancel resolves questionnaire id or falls back to entity_id.
        return item?.document_type === "VQA" &&
          item?.request_status === "Pending" ? (
          <LibraryRequestViewDetailsMenu path={detailsPath} />
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            <MenuDropdownAccordingToStatuses
              status={item?.request_status}
              type={item?.document_type}
              docId={item?.document_id}
              isDocLibrary={true}
              isMyRFI={false}
              sponsorRfiResponseId={item?.entity_id}
              questionnaireId={item?.hc_questionnaire_id}
              isMyDocs={true}
              details={item}
              refetch={refetch}
              isLibraryBackendAdmin={false}
              libraryRequestId={item?.request_id}
              libraryRequestDetailsPath={detailsPath}
            />
          </div>
        );
      },
    };

    return [
      {
        key: "document_type",
        header: "Type",
        sortable: false,
        width: "2%",
        minWidth: SIZE_TABLE_COL_MIN.document_type_icon,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900",
        render: (item) => renderDocumentTypeCell(item),
      },
      ...orgColumns,
      {
        key: "capabilities",
        header: "Scope",
        sortable: false,
        width: "14%",
        minWidth: SIZE_TABLE_COL_MIN.scope,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        render: (item) => (
          <ScopeWithAdditionalTags
            services={item.capabilities}
            additionalScope={item.additional_scope}
            showAdditionalScope={shouldShowAdditionalScopeOnLibraryItem(item)}
            capabilitiesProps={{
              icon: true,
              showLimited: false,
              badgesPerRow: 3,
            }}
          />
        ),
      },
      {
        key: "location",
        header: "Location",
        sortable: true,
        width: "8%",
        minWidth: SIZE_TABLE_COL_MIN.location,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900",
        render: (item) => {
          const loc = getLibraryRequestLocationDisplay(item);
          return loc ? (
            <span className="break-words">{loc}</span>
          ) : (
            <span className="text-gray-400">--</span>
          );
        },
      },
      ...(showRiskColumn
        ? [
            {
              key: "risk",
              header: "Risk Summary",
              sortable: true,
              width: "10%",
              cellClassName: "text-gray-800",
              render: (item) => (
                <RiskSummaryCell summary={getLibraryRequestRiskSummary(item, summaryMap)} />
              ),
            },
          ]
        : []),
      {
        key: "request_status",
        header: "Status",
        sortable: true,
        width: LIBRARY_REQ_METADATA_COL_WIDTH,
        minWidth: SIZE_TABLE_COL_MIN.status,
        cellClassName: "text-center",
        render: (item) => (
          <GridStatusDotCell status={item?.request_status} />
        ),
      },
      {
        key: "published_on",
        header: "Published",
        sortable: true,
        width: LIBRARY_REQ_METADATA_COL_WIDTH,
        minWidth: SIZE_TABLE_COL_MIN.published,
        cellClassName: "text-center",
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const pub = getLibraryRequestPublishedDisplay(item);
          return pub ? (
            <span className="text-gray-500">{pub}</span>
          ) : (
            <span className="text-gray-300">--</span>
          );
        },
      },
      {
        key: "requested_on",
        header: "Request Date",
        sortable: true,
        width: LIBRARY_REQ_METADATA_COL_WIDTH,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: "text-center",
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) =>
          item?.requested_on ? (
            formatGridDate(item.requested_on)
          ) : (
            <span className="text-gray-400">--</span>
          ),
      },
      {
        key: "released_on",
        header: "Release Date",
        sortable: true,
        width: LIBRARY_REQ_METADATA_COL_WIDTH,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: "text-center",
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) =>
          item?.released_on ? (
            formatGridDate(item.released_on)
          ) : (
            <span className="text-gray-300">--</span>
          ),
      },
      {
        key: "cycle_time",
        header: "Cycle Time",
        sortable: true,
        width: LIBRARY_REQ_METADATA_COL_WIDTH,
        minWidth: SIZE_TABLE_COL_MIN.cycle,
        cellClassName: "text-center",
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const c = getLibraryRequestCycleTimeDisplay(item);
          return c ? (
            <span className="text-gray-500">{c}</span>
          ) : (
            <span className="text-gray-300">--</span>
          );
        },
      },
      archiveCommentColumn,
      actionsColumn,
    ].filter(Boolean);
  }, [
    audience,
    approve,
    approveError,
    refetch,
    reject,
    rejectError,
    rejectIsError,
    renderDocumentTypeCell,
    showRiskColumn,
    summaryMap,
    navigateToHref,
    sponsorHrefForLibraryItem,
    vendorHrefForLibraryItem,
  ]);

  const displayData = useMemo(() => {
    const activeKey = Object.keys(sortConfig || {})[0];
    if (activeKey !== "risk" || !showRiskColumn) return data;
    const direction = sortConfig.risk?.direction || "desc";
    return [...(data || [])].sort(
      makeRiskSummarySortComparator(summaryMap, direction, getLibraryRequestRiskSummary)
    );
  }, [data, sortConfig, summaryMap, showRiskColumn]);

  return (
    <RfiActionToastProvider>
    <div className="w-full min-w-0 max-w-full">
      <CustomTable
        columns={columns}
        data={displayData}
        sortConfig={sortConfig}
        onSort={onSort}
        fixedColumnWidths
        emptyMessage={emptyMessage}
        emptyDescription={emptyDescription}
        isSearchActive={isSearchActive}
        edgeToEdge
        className={className}
        rowKey={rowKey}
        getHeaderCellPaddingClass={(colIndex, totalCols) =>
          getRfiGridCellPaddingClass(colIndex, totalCols, true)
        }
        getBodyCellPaddingClass={(colIndex, totalCols) =>
          getRfiGridCellPaddingClass(colIndex, totalCols, false)
        }
        scrollWrapperClassName="bg-white"
        tableDivideClassName="divide-gray-200"
        theadClassName="bg-gray-50 [&_th]:align-middle [&_th]:min-h-[3rem]"
        tbodyClassName="divide-y divide-gray-200 bg-white"
        useDividedBodyRows
        getRowClassName={(item) =>
          item?.request_status === "Archived" ? "opacity-60" : ""
        }
        onRowClick={(item) => {
          const path = resolveLibraryRequestDetailsPath(item);
          if (path) navigate(path);
        }}
      />
    </div>
    </RfiActionToastProvider>
  );
}
